import React, { useState } from "react";
import { Close, Logo, SultanLogo } from "../../assets/icons/Icons";
import { useNavigate } from "react-router-dom";
import { mainItems, supportItems } from "../../assets/data/sidebar";
import SidebarButton from "../atoms/buttons/SidebarButton";
import { getRouteSegment } from "../../lib/utils/funcs";
import { useHospDocData } from "../../lib/contexts/HospitalDoctorContext";
import { useUserData } from "../../lib/contexts/UserContext";
import { FaRegUserCircle } from "react-icons/fa";
import { gunasheelaHidingDetails } from "../../lib/utils/constants";
import { useFeatureFlags } from "../../lib/contexts/FeatureFlagContext";
import { getWaLeads } from "../../lib/apis/waLeads";
import { format } from "date-fns";
import { useEffect } from "react";

const MobileSidebar = ({
  visible,
  setVisible,
}: {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const navigate = useNavigate();
  const { hospitalID, hospData } = useHospDocData();
  const { userData } = useUserData();
  const { wa_lead_collection_via_dropoffs } = useFeatureFlags();
  const [isHovered, setIsHovered] = useState("");
  const [waLeadsCount, setWaLeadsCount] = useState(0);

  useEffect(() => {
    if (!hospitalID || !wa_lead_collection_via_dropoffs) return;
    const fetchCount = async () => {
      try {
        const dateStr = format(new Date(), "yyyy-MM-dd");
        const res = await getWaLeads(hospitalID, dateStr, 1, 1, "open");
        if (res?.status === 200) {
          const result = res.data?.result || res.data?.data || res.data;
          if (result?.pagination?.totalRecords != null) setWaLeadsCount(result.pagination.totalRecords);
          else if (result?.count != null) setWaLeadsCount(result.count);
          else if (Array.isArray(result)) { const f = result[0]; setWaLeadsCount(f?.count || f?.leads?.length || result.length || 0); }
        }
      } catch {}
    };
    fetchCount();
    const handleUpdate = () => fetchCount();
    window.addEventListener("wa-leads-updated", handleUpdate);
    return () => window.removeEventListener("wa-leads-updated", handleUpdate);
  }, [hospitalID, wa_lead_collection_via_dropoffs]);

  // ✅ Add same patient-hiding logic from Desktop Sidebar
  const isRestrictedUser =
    userData?.role === "FloorReceptionist" ||
    (userData?.role === "Receptionist" &&
      gunasheelaHidingDetails.includes(hospitalID || ""));

  // ✅ Filter “Patients” item for restricted users
  const filteredMainItems = isRestrictedUser
    ? mainItems.filter((item) => item.name !== "Patients")
    : mainItems;

  return (
    <>
      <aside
        className={`fixed top-0 left-0 h-full w-4/5 max-w-sm bg-white xl:hidden flex flex-col overflow-y-auto z-50
          transform transition-transform duration-300 ease-in-out ${
            visible ? "animate-slideIn" : "animate-slideOut"
          }`}
      >
        <button
          className="m-4 flex flex-row-reverse"
          onClick={() => setVisible(false)}
        >
          <Close />
        </button>
        <div className="bg-white flex flex-col h-full px-6">
          <div className="flex flex-col">
            <p className="ml-5 mb-3 uppercase text-[11px] font-medium">Main</p>
            <div className="flex flex-col">
              {(userData?.role === "Doctor"
                ? filteredMainItems.map(
                    (
                      item // ✅ use filteredMainItems
                    ) =>
                      item.name === "Doctors"
                        ? {
                            name: "Profile",
                            icon: <FaRegUserCircle className="w-6 h-6" />,
                            hoverIcon: <FaRegUserCircle className="w-6 h-6" />,
                          }
                        : item
                  )
                : filteredMainItems
              ) // ✅ use filteredMainItems here too
                .map((item, index) => (
                  <SidebarButton
                    key={index}
                    index={index}
                    isHovered={isHovered}
                    setIsHovered={setIsHovered}
                    name={item.name}
                    icon={item.icon}
                    hoverIcon={item.hoverIcon}
                    onPress={() => {
                      navigate(
                        `/${hospitalID}/${item.name
                          .toLowerCase()
                          .replace(/[^a-z0-9]/g, "")}`
                      );
                      setVisible(false);
                    }}
                    isSelected={
                      getRouteSegment(2) ===
                      item.name.toLowerCase().replace(/[^a-z0-9]/g, "")
                    }
                    rightIcon={
                      item.name === "WA Leads" && waLeadsCount > 0
                        ? <span className="relative -top-3 -right-2 animate-bounce bg-blue-500 text-white text-[9px] font-bold w-5 h-5 flex items-center justify-center rounded-md shadow-sm">{waLeadsCount}</span>
                        : undefined
                    }
                  />
                ))}
            </div>
          </div>

          <div className="mt-8 flex flex-col">
            <p className="ml-5 mb-3 uppercase text-[11px] font-medium">
              Support
            </p>
            <div className="flex flex-col">
              {supportItems.map((item, index) => (
                <SidebarButton
                  key={index}
                  index={index}
                  isHovered={isHovered}
                  setIsHovered={setIsHovered}
                  name={item.name}
                  icon={item.icon}
                  hoverIcon={item.hoverIcon}
                  onPress={() => {
                    navigate(
                      `/${hospitalID}/${item.name.toLowerCase().split(" ")[0]}`
                    );
                    setVisible(false);
                  }}
                  isSelected={
                    getRouteSegment(2) === item.name.split(" ")[0].toLowerCase()
                  }
                />
              ))}
            </div>
          </div>

          <a
            href="https://www.youtube.com/playlist?list=PLbQxMkdQD7qDJun4vhI0Drognf0XWu7aY"
            target="_blank"
            rel="noopener noreferrer"
          >
            <button
              className="group
             flex items-center justify-center
             font-sans text-lg
             bg-gradient-to-b from-blue-600 to-blue-400
             text-white
             py-2 px-3
             ml-3
             mb-4
             mt-10
             w-full
             rounded-full
             shadow-md
             transform transition-all duration-300
             hover:-translate-y-1 hover:shadow-lg
             active:scale-95 active:shadow-sm"
            >
              <div
                className="flex items-center justify-center
               w-8 h-8
               rounded-full
               bg-white/20
               mr-2
               transition-all duration-300
               group-hover:bg-white/50"
              >
                <svg
                  className="w-4 h-4 fill-current
                 transition-transform duration-500 ease-in-out
                 origin-center
                 group-hover:rotate-45"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                >
                  <path d="M0 0h24v24H0z" fill="none" />
                  <path d="M1.946 9.315c-.522-.174-.527-.455.01-.634l19.087-6.362c.529-.176.832.12.684.638l-5.454 19.086c-.15.529-.455.547-.679.045L12 14l6-8-8 6-8.054-2.685z" />
                </svg>
              </div>
              <span className="block text-[1rem] font-[Inter] ml-2 transition-all text-left duration-300">
                Tutorial
              </span>
            </button>
          </a>
        </div>
      </aside>

      {visible && (
        <div
          className="fixed inset-0 bg-black/50 z-40 xl:hidden"
          onClick={() => setVisible(false)}
        ></div>
      )}
    </>
  );
};

export default MobileSidebar;
